import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import type { QuarterlyReportRecord, QuarterlyReportSummary } from '../types/quarterlyReport';

/**
 * Firestore path: `users/{uid}/quarterly_reports/{reportId}`.
 *
 * For v1 we assume released reports are what the member sees. Drafts and
 * in-review reports still live in the same collection so ops can hydrate
 * them — we filter client-side until/if we need Firestore rules to hide them.
 */

const REPORTS_SUBCOLLECTION = 'quarterly_reports';

export async function listQuarterlyReportSummaries(userId: string): Promise<QuarterlyReportSummary[]> {
  const reportsRef = collection(db, 'users', userId, REPORTS_SUBCOLLECTION);
  // The member-read rule (users/{uid}/quarterly_reports) only permits reading
  // docs where status == 'released'. Firestore rejects a query whose constraints
  // don't provably match that rule, so the query MUST carry the same equality
  // filter — an unfiltered list throws permission-denied even on an empty
  // collection. Equality-only (no orderBy) needs no composite index; we sort
  // client-side by quarterLabel below.
  const snap = await getDocs(query(reportsRef, where('status', '==', 'released')));
  return snap.docs
    .map((document) => {
      const data = document.data() as QuarterlyReportRecord;
      if (data.status !== 'released') return null;
      return {
        id: document.id,
        quarterLabel: data.quarterLabel,
        headline: data.executiveSummary?.headline || '',
        status: data.status,
        releasedAt: data.releasedAt,
        reviewingClinician: data.reviewingClinician,
        signingClinician: data.signingClinician,
        membershipTier: data.membershipTier,
      } as QuarterlyReportSummary;
    })
    .filter((summary): summary is QuarterlyReportSummary => Boolean(summary))
    .sort((left, right) => (right.quarterLabel || '').localeCompare(left.quarterLabel || ''));
}

export async function getQuarterlyReport(userId: string, reportId: string): Promise<QuarterlyReportRecord | null> {
  const reportRef = doc(db, 'users', userId, REPORTS_SUBCOLLECTION, reportId);
  const snap = await getDoc(reportRef);
  if (!snap.exists()) return null;
  const data = snap.data() as QuarterlyReportRecord;
  if (data.status !== 'released') return null;
  return { ...data, id: snap.id };
}

/**
 * Deterministic sample report — shown on `/reports/sample` so members can
 * see what a finished quarterly review looks like before their first one
 * is released. Content based on `docs/markdown/clinical/MELIO_QUARTERLY_REPORT_EXAMPLE.md`.
 */
export const SAMPLE_QUARTERLY_REPORT: QuarterlyReportRecord = {
  id: 'sample',
  userId: 'sample',
  quarterLabel: 'Q2 2026',
  membershipTier: 'core',
  preparedBy: 'Melio Clinical Team',
  reviewingClinician: 'Dr Example',
  signingClinician: 'Dr Helena Example',
  status: 'released',
  executiveSummary: {
    headline:
      "This quarter's review suggests a moderately elevated long-term cardiovascular risk profile, driven mainly by family history of premature coronary disease, elevated Lp(a), and ApoB above ideal range. Biological age pace is slightly faster than desired, but there are no immediate red flags requiring urgent intervention.",
    conclusions: [
      'Your inherited cardiovascular risk remains the main prevention focus.',
      'Lipid-related risk is actionable and should not be deferred.',
      'Glucose regulation is acceptable today, but should continue to be monitored.',
      'No cancer or neurological red flags emerged this quarter from the current dataset.',
    ],
    topPriorities: [
      'Complete the recommended lipid-focused follow-up plan.',
      'Repeat the agreed biomarker subset to assess response to intervention.',
      'Maintain the annual monitoring schedule rather than adding broad extra testing.',
    ],
  },
  baselineContext: {
    familyHistoryBullets: [
      'Father had myocardial infarction at age 52.',
      'Paternal grandfather died of stroke at 61.',
      'Mother has type 2 diabetes diagnosed in her late 50s.',
      'No strong hereditary cancer pattern identified from current family-history map.',
    ],
    geneticsBullets: [
      'Polygenic cardiovascular risk: elevated.',
      'Genetics suggests increased relevance of lipid surveillance and early ApoB / Lp(a) monitoring.',
      'No stand-alone diagnostic genetics finding used in isolation.',
    ],
    lifestyleBullets: [
      'High work stress.',
      'Inconsistent sleep.',
      'Regular exercise, but variable intensity.',
      'No current smoking.',
    ],
    dnaInfluencedDecisions: [
      'Test Lp(a) early rather than waiting until later-life routine screening.',
      'Prioritise ApoB and lipid surveillance over broad, low-yield exploratory testing.',
      'Keep cardiovascular imaging as a future escalation pathway rather than a default first-quarter action.',
      'Maintain annual biological age tracking only as a trend metric, not as a primary decision-maker.',
    ],
  },
  whatHappened: {
    dataReceived: [
      'Baseline blood panel completed.',
      'Biological age add-on completed via DunedinPACE / TruDiagnostic.',
      'Updated structured family history submitted.',
      'Wearable summary reviewed for sleep, resting heart rate, and recovery pattern.',
    ],
    testsCompleted: [
      { test: 'ApoB', result: '104 mg/dL', interpretation: 'Above ideal for your risk context.' },
      { test: 'Lp(a)', result: '92 nmol/L', interpretation: 'Elevated and relevant given family history.' },
      { test: 'LDL-C', result: '149 mg/dL', interpretation: 'Higher than ideal.' },
      { test: 'HDL-C', result: '54 mg/dL', interpretation: 'Acceptable.' },
      { test: 'Triglycerides', result: '118 mg/dL', interpretation: 'Acceptable.' },
      { test: 'HbA1c', result: '5.4%', interpretation: 'Within normal range.' },
      { test: 'hs-CRP', result: '1.8 mg/L', interpretation: 'Mildly elevated, non-specific.' },
      { test: 'Fasting glucose', result: '5.0 mmol/L', interpretation: 'Normal.' },
      { test: 'DunedinPACE', result: '1.07', interpretation: 'Slightly faster pace of aging than desired.' },
    ],
    otherUpdates: [
      'Average sleep duration fell during a high-workload month.',
      'No major new symptoms reported.',
      'No new external reports uploaded this quarter.',
    ],
  },
  clinicianInterpretation: [
    "This quarter's results reinforce the current view that the main actionable prevention area is cardiovascular risk reduction.",
    'The most important finding is not one isolated lab value. It is the combination of family history of premature cardiovascular disease, elevated Lp(a), ApoB above ideal, and the genetics context that increases the relevance of lipid monitoring.',
    'That combination supports a more proactive lipid surveillance strategy than would usually be recommended for a lower-risk person of the same age.',
    'The biological age result is useful as a longitudinal trend marker, but it should not be over-weighted. A DunedinPACE of 1.07 is not the main decision driver this quarter — it becomes more useful when repeated over time alongside biomarker changes and lifestyle consistency.',
    'There is no current evidence from this quarter that justifies broad imaging, specialist cancer testing, or highly expanded panels. Those would add complexity and cost without clearly improving decision quality today.',
  ],
  actions: {
    recommendedNow: [
      'Discuss a lipid-lowering strategy with your clinician, especially in light of ApoB and Lp(a).',
      'Tighten the near-term focus on sleep regularity, exercise consistency, and dietary adherence.',
      'Keep tracking cardiovascular markers rather than expanding into unrelated testing.',
    ],
    notRecommendedNow: [
      'No routine full-body imaging.',
      'No broad cancer screening expansion on current evidence.',
      'No repetition of the full baseline panel before the agreed follow-up window.',
    ],
    escalateIf: [
      'New exertional chest symptoms appear.',
      'Blood pressure rises persistently.',
      'Repeat biomarkers worsen despite intervention.',
    ],
  },
  testingSchedule: {
    nextQuarter: ['Repeat ApoB.', 'Repeat LDL-C.', 'Repeat hs-CRP.', 'Blood pressure review.'],
    laterThisYear: [
      'Repeat HbA1c at standard cadence.',
      'Repeat biological age add-on only if you want trend tracking and after enough time has passed to make the comparison meaningful.',
    ],
    notCurrentlyDue: ['Advanced imaging.', 'Hereditary cancer panel.', 'Expanded endocrine testing.'],
  },
  watchlist: [
    {
      title: 'Lipid-related cardiovascular risk',
      detail: 'This remains the main ongoing issue because it is supported by genetics context, family history, and current biomarkers.',
    },
    {
      title: 'Pace-of-aging trend',
      detail: 'Useful as a monitoring signal, but only over time. One quarter is not enough to over-interpret.',
    },
    {
      title: 'Stress / sleep load',
      detail: 'This is relevant because it may influence inflammatory markers, recovery, and long-term risk trajectory.',
    },
  ],
  nextReviewQuestion:
    'Are the cardiovascular markers improving in response to the agreed plan, or is a stronger escalation pathway needed?',
  clinicianNote:
    'This report is designed to help you understand what matters now, what can wait, and what Melio is watching over time. It is intended to be clinically useful and actionable, not a dump of disconnected data points.',
};
